import { Injectable } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthService } from './services/auth.service';
import { Observable } from 'rxjs';
import { map, take, filter, switchMap } from 'rxjs/operators';

/**
 * Guard de autenticación para proteger rutas de acceso no autorizado.
 *
 * Verifica si el usuario está autenticado usando AuthService.isLoggedIn$ Observable.
 * Si no está autenticado, redirige a la página de login con el parámetro redirect.
 *
 * Este guard solo verifica autenticación básica. Los guards de roles específicos
 * (BoRolesCheckService, BtbRolesCheckService) manejan la autorización a nivel de componente.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthGuardService implements CanActivate {
  constructor(
    private router: Router,
    private authService: AuthService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    // Esperar a que AuthService termine de inicializar completamente
    return this.authService.initialized$.pipe(
      filter(initialized => initialized === true),  // Esperar hasta que sea true
      take(1),
      switchMap(() => this.authService.isLoggedIn$),  // Luego verificar si está logueado
      take(1),
      map(isLoggedIn => {
        if (isLoggedIn) {
          // Usuario autenticado - permitir acceso
          return true;
        } else {
          // Usuario no autenticado - redirigir a login con URL de retorno
          const returnUrl = state.url;

          // Prevenir loops de redirección
          if (returnUrl.includes('/onboarding/login')) {
            return this.router.createUrlTree(['/onboarding/login']);
          }

          // Redirigir a login con parámetro redirect
          return this.router.createUrlTree(['/onboarding/login'], {
            queryParams: { redirect: encodeURIComponent(returnUrl) }
          });
        }
      })
    );
  }
}
